// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Optix Schema Debug card (global admin only).
 *
 * Only the i9processor Lambda may invoke the Optix Java Lambda, and the only way
 * to reach it is via the I-9 status queue. So "Run" publishes an i9_debug job
 * (same path as a retry); the Lambda inspects the Optix schema and writes a report
 * to S3, which this card polls and renders.
 */
import { useState } from 'react'
import type { FC } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui'
import { Bug, RefreshCw, Loader2, ChevronRight, AlertTriangle } from 'lucide-react'
import { useServices } from '@/app-shell/services'
import { formatTimestamp } from './format'

interface OptixField {
  name: string | null
  type: number | string | null
  size: number | null
}

interface OptixTable {
  name: string | null
  fieldCount: number
  fields: OptixField[]
}

interface OptixScreen {
  name: string | null
  id: string | null
  storageId: number | string | null
  table: OptixTable | null
  error: string | null
}

interface OptixScreenFailure {
  screenName: string | null
  error: string | null
}

interface OptixDebugReport {
  available: boolean
  jobId: string | null
  generatedAt: string | null
  screenCount: number
  tableCount: number
  failureCount: number
  screens: OptixScreen[]
  tables: OptixTable[]
  failures: OptixScreenFailure[]
}

interface OptixDebugCardProps {
  orgId: string
  workspaceId: string
}

export const OptixDebugCard: FC<OptixDebugCardProps> = ({ orgId, workspaceId }) => {
  const services = useServices()
  const apiClient = services.getClient()
  const basePath = `/org/${orgId}/ws/${workspaceId}/i9`

  // Job id of the run queued from this card; while a fresh report hasn't landed,
  // the GET is polled and the button shows as running.
  const [queuedJobId, setQueuedJobId] = useState<string | null>(null)

  const { data: report } = useQuery({
    queryKey: ['i9', 'optix-debug', orgId, workspaceId],
    // Poll while a queued run hasn't reported back yet; otherwise just hold the result.
    refetchInterval: queuedJobId ? 3000 : false,
    queryFn: async (): Promise<OptixDebugReport> => {
      const response = await apiClient.get<OptixDebugReport>(`${basePath}/optix-debug`)
      const data = response.data
      // The queued run has landed once the stored report carries its job id.
      if (queuedJobId && data.jobId === queuedJobId) setQueuedJobId(null)
      return data
    },
  })

  const runMutation = useMutation({
    mutationFn: async (): Promise<string> => {
      const response = await apiClient.post<{ jobId: string }>(`${basePath}/optix-debug`, {})
      return response.data.jobId
    },
    onSuccess: (jobId) => setQueuedJobId(jobId),
  })

  const isRunning = runMutation.isPending || queuedJobId !== null

  return (
    <Card data-testid="i9-optix-debug">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bug className="h-5 w-5" />
              Optix Schema Debug
            </CardTitle>
            <CardDescription>
              Inspect Optix screens, tables, and fields via the i9processor Lambda.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => runMutation.mutate()} disabled={isRunning}>
            {isRunning ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            {isRunning ? 'Running…' : 'Run debug'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {runMutation.isError && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
            Failed to queue debug run: {(runMutation.error as Error).message}
          </div>
        )}

        {!report?.available && !isRunning && (
          <p className="text-sm text-muted-foreground">
            No report yet. Run the debug to inspect the Optix schema.
          </p>
        )}

        {report?.available && (
          <>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge variant="secondary">{report.screenCount} screens</Badge>
              <Badge variant="secondary">{report.tableCount} tables</Badge>
              <Badge variant={report.failureCount > 0 ? 'destructive' : 'secondary'}>
                {report.failureCount} failures
              </Badge>
              <span className="text-muted-foreground">
                Last run: {formatTimestamp(report.generatedAt)}
              </span>
            </div>

            {report.screens.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Screens</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Screen name</TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Storage</TableHead>
                      <TableHead>Backing table</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.screens.map((screen, i) => (
                      <TableRow key={screen.name ?? i}>
                        <TableCell className="font-mono">{screen.name ?? '—'}</TableCell>
                        <TableCell className="font-mono text-muted-foreground">{screen.id ?? '—'}</TableCell>
                        <TableCell className="font-mono text-muted-foreground">{screen.storageId ?? '—'}</TableCell>
                        <TableCell className="font-mono text-muted-foreground">
                          {screen.error ? (
                            <span className="text-amber-700">{screen.error}</span>
                          ) : (
                            screen.table?.name ?? '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {report.tables.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Tables</p>
                {report.tables.map((table, i) => (
                  <Collapsible key={table.name ?? i}>
                    <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted">
                      <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
                      <span className="font-mono">{table.name ?? '(unnamed)'}</span>
                      <span className="text-muted-foreground">· {table.fieldCount} fields</span>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Field</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Size</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {table.fields.map((field, j) => (
                            <TableRow key={field.name ?? j}>
                              <TableCell className="font-mono">{field.name ?? '—'}</TableCell>
                              <TableCell>{field.type ?? '—'}</TableCell>
                              <TableCell>{field.size ?? '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}

            {report.failures.length > 0 && (
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-sm font-medium text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                  Screens that could not be inspected
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Screen</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.failures.map((failure, i) => (
                      <TableRow key={failure.screenName ?? i}>
                        <TableCell className="font-mono">{failure.screenName ?? '—'}</TableCell>
                        <TableCell className="text-muted-foreground">{failure.error ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
